import { useRef, useState } from "react";
import { Upload, Loader2, X, Check, Images } from "lucide-react";
import { toast } from "sonner";
import { api, photoUrl } from "@/lib/api";

// Bulk-upload a batch of client photos, then group them into before/after pairs.
export default function BulkUploadPairs({ onPublished }) {
  const fileRef = useRef(null);
  const [tray, setTray] = useState([]); // [{id}]
  const [uploading, setUploading] = useState(false);
  const [before, setBefore] = useState("");
  const [after, setAfter] = useState("");
  const [caption, setCaption] = useState("");
  const [publishing, setPublishing] = useState(false);
  const [createdCount, setCreatedCount] = useState(0);

  const onFiles = async (files) => {
    const list = Array.from(files || []);
    if (!list.length) return;
    setUploading(true);
    let ok = 0;
    for (const file of list) {
      try {
        const fd = new FormData();
        fd.append("file", file);
        fd.append("label", "client");
        const { data } = await api.post("/photos/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        setTray((t) => [...t, { id: data.id }]);
        ok += 1;
      } catch (e) {
        toast.error(`${file.name}: ${e?.response?.data?.detail || "upload failed"}`);
      }
    }
    setUploading(false);
    if (ok) toast.success(`${ok} photo${ok !== 1 ? "s" : ""} uploaded`);
  };

  const pick = (id) => {
    if (before === id) return setBefore("");
    if (after === id) return setAfter("");
    if (!before) return setBefore(id);
    if (!after) return setAfter(id);
    // both set — replace the "after"
    setAfter(id);
  };

  const removeFromTray = (id) => {
    setTray((t) => t.filter((p) => p.id !== id));
    if (before === id) setBefore("");
    if (after === id) setAfter("");
  };

  const addPair = async () => {
    if (!before || !after) return;
    setPublishing(true);
    try {
      await api.post("/gallery", {
        booking_id: null,
        before_photo_id: before,
        after_photo_id: after,
        caption,
        published: true,
      });
      setTray((t) => t.filter((p) => p.id !== before && p.id !== after));
      setBefore("");
      setAfter("");
      setCaption("");
      setCreatedCount((n) => n + 1);
      toast.success("Pair added to showcase");
      onPublished?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not add pair");
    } finally {
      setPublishing(false);
    }
  };

  const badge = (id) => (before === id ? "B" : after === id ? "A" : null);

  return (
    <div className="cj-card p-5" data-testid="bulk-upload-pairs">
      <div className="flex items-center gap-2 mb-1">
        <Images className="h-4 w-4 text-[#ff6b00]" />
        <div className="font-mono text-[11px] uppercase tracking-[0.2em] text-slate-500">Bulk upload</div>
      </div>
      <p className="text-sm text-slate-400">
        Select a whole batch of client photos, then tap one for <span className="text-[#ff6b00] font-semibold">Before</span> and one for{" "}
        <span className="text-emerald-400 font-semibold">After</span> to build each pair.
      </p>

      <div className="mt-4 flex flex-wrap items-center gap-3">
        <button
          type="button"
          onClick={() => fileRef.current?.click()}
          disabled={uploading}
          data-testid="bulk-choose-files-button"
          className="cj-btn-primary px-5 py-2.5 rounded-full text-sm inline-flex items-center gap-2 disabled:opacity-50"
        >
          {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
          {uploading ? "Uploading…" : "Choose photos"}
        </button>
        {createdCount > 0 && (
          <span className="cj-pill bg-emerald-500/10 text-emerald-300 border border-emerald-500/30 inline-flex items-center gap-1">
            <Check className="h-3.5 w-3.5" /> {createdCount} pair{createdCount !== 1 ? "s" : ""} added
          </span>
        )}
        <input
          ref={fileRef}
          type="file"
          accept="image/*"
          multiple
          hidden
          data-testid="bulk-file-input"
          onChange={(e) => onFiles(e.target.files)}
        />
      </div>

      {tray.length > 0 && (
        <>
          <div className="mt-5 grid grid-cols-3 sm:grid-cols-5 gap-3" data-testid="bulk-tray">
            {tray.map((p) => {
              const b = badge(p.id);
              return (
                <div key={p.id} className="relative group">
                  <button
                    type="button"
                    onClick={() => pick(p.id)}
                    data-testid={`bulk-tile-${p.id}`}
                    className={`block w-full aspect-square rounded-xl overflow-hidden border-2 transition ${
                      b === "B" ? "border-[#ff6b00]" : b === "A" ? "border-emerald-400" : "border-white/10 hover:border-white/30"
                    }`}
                  >
                    <img src={photoUrl(p.id)} alt="" className="w-full h-full object-cover" />
                  </button>
                  {b && (
                    <span
                      className={`absolute top-1.5 left-1.5 h-6 w-6 rounded-full text-xs font-bold flex items-center justify-center text-[#0b0f17] ${
                        b === "B" ? "bg-[#ff6b00]" : "bg-emerald-400"
                      }`}
                    >
                      {b}
                    </span>
                  )}
                  <button
                    type="button"
                    onClick={() => removeFromTray(p.id)}
                    data-testid={`bulk-remove-${p.id}`}
                    className="absolute top-1.5 right-1.5 h-6 w-6 rounded-full bg-black/60 hover:bg-black/80 flex items-center justify-center text-white opacity-0 group-hover:opacity-100 transition"
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                </div>
              );
            })}
          </div>

          <input
            value={caption}
            onChange={(e) => setCaption(e.target.value)}
            placeholder="Caption for this pair (optional)"
            data-testid="bulk-caption-input"
            className="mt-4 w-full bg-[#0f1522] border border-white/10 rounded-xl px-4 py-2.5 text-sm text-slate-200 focus:outline-none focus:border-[#ff6b00]"
          />
          <button
            onClick={addPair}
            disabled={!before || !after || publishing}
            data-testid="bulk-add-pair-button"
            className="cj-btn-primary mt-3 px-5 py-2.5 rounded-full text-sm inline-flex items-center gap-2 disabled:opacity-50"
          >
            {publishing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
            Add this pair
          </button>
        </>
      )}
    </div>
  );
}
